/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Paper from '@material-ui/core/Paper';
// import { DateTime } from 'luxon';
import qs from 'query-string';
import React, { useEffect, useState } from "react";
import { useQueryClient } from 'react-query';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Alert, Card, CardFooter, CardHeader, Col, Container, Row, Spinner, Table
} from "reactstrap";
import CustomPagination from '../components/CustomPagination';
import Header from "../components/Headers/Header";
// import Tooltip from '../components/Tooltip';
import { useAllEarningsTranscriptsList } from '../hooks/useAllEarningsTranscriptsList'
import { useQueryParams } from '../hooks/useQueryParams';
// import { secondsToTimeString } from '../utils/secondsToTimeString';
// import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';
// import { toTitleCase } from '../utils/toTitleCase';
import { updateQueryParams } from '../utils/updateQueryParams';
import { DateTimeFormatOptions } from "luxon";

const RESULTS_PER_PAGE = 10;

const EarningsCall: React.FC = () => {
  const query = useQueryParams()
  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })

  const [shouldFetchData, setShouldFetchData] = useState(false);



  const [searchTerm, setSearchTerm] = useState(() => {
    const searchTerm = query.get('searchTerm')
    if (searchTerm) {
      // console.log("searchTerm----", searchTerm);
      return searchTerm
    }
    return ''
  })
  const queryClient = useQueryClient()
  const location = useLocation()
  const router = useHistory()

  const {
      isError,
      isLoading,
      data,
      refetch,
    } = useAllEarningsTranscriptsList(pageTable, RESULTS_PER_PAGE, searchTerm, shouldFetchData)

  // console.log("data in page ----", data?.data);

  useEffect(() => {
    handleConfSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTable])


  const handleConfSearch = () => {
    if (isLoading) {
      return
    }
    queryClient.removeQueries(["transcripts", "list", "searchTerm"])
    refetch()
  }

  useEffect(() => {
    if (shouldFetchData) {
      handleConfSearch();
      setShouldFetchData(false); // Reset the flag after search is performed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData]);
  
  const handlePressEnter = () => {
    setPageTable(1)
    
    // Convert search term to uppercase
    const upperCaseSearchTerm = searchTerm.toUpperCase();
    setSearchTerm(upperCaseSearchTerm);

    setShouldFetchData(true); 
    const q = qs.parse(location.search)
    q.searchTerm = upperCaseSearchTerm
    if (upperCaseSearchTerm.trim().length === 0) {
      delete q.searchTerm
    }
    // q.page = `${pageTable}`
    updateQueryParams(qs.stringify(q), router, location.pathname)
  }


  function formatDate(dateString: string) {
    const options: DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const formattedDate = new Date(dateString).toLocaleString("en-US", options);
    return formattedDate;
  }

  let content: JSX.Element | null = null

  if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    );
  } else if (!searchTerm) {
    content = (
      <Alert color="warning">
        Please enter a search term
      </Alert>
    );
  } else if (isLoading) {
    content = <Spinner />
  } else if (!data || data?.data?.length === 0) {
    content = (
      <Alert color="warning">
        No data available
      </Alert>
    );
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light" style={{ textAlign: 'center' }}>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Time</th>
              <th scope="col">Quarter</th>
              <th scope="col">Year</th>
              <th scope="col">ID</th>
            </tr>
          </thead>
          <tbody>
            {
            data?.data.map((ex: any, i: number) => (
                <tr key={i}>
                  <th scope="row" style={{ textAlign: 'center' }}>{ex.title}</th>
                  <th scope="row" style={{ textAlign: 'center' }}>{formatDate(ex.time)}</th>
                  <th scope="row" style={{ textAlign: 'center' }}>{ex.quarter}</th>
                  <th scope="row" style={{ textAlign: 'center' }}>{ex.year}</th>
                  <th scope="row" style={{ textAlign: 'center' }}> <Link to={`/admin/earningsCall/transcript?transcriptId=${ex.id}`} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-link mr-1" />
                    </Link></th>
                  {/* <td>{toTitleCase(ex.user.name)}</td> */}
                  {/* <td>{toTitleCase(ex.status)}
                    {ex.errorMessage &&
                      <span className='ml-2'><Tooltip text={`Error: ${ex.errorMessage}`} /></span>}
                  </td> */}
                </tr>
              ))
            }
          </tbody>
        </Table>
        <CardFooter className="py-4">
          <nav aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data?.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    )
  }
  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <div className="input-group-alternative input-group">
                      <div className="input-group-prepend">
                        <span onClick={handlePressEnter} className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                      <input
                        value={searchTerm}
                        onChange={e => {
                          setSearchTerm(e.target.value)
                        }}
                        onKeyDown={e => {
                          if (e.key.toLowerCase() === 'enter') {
                            handlePressEnter()
                          }
                        }}
                        placeholder="Search for any company ticker"
                        type="text"
                        className="form-control"
                      />
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <Paper className="px-4">
                <div className="mb-3">
                  <h4 style={{ display: 'inline' }}>Transcripts List</h4>
                </div>
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EarningsCall;
